/**
 * TPT Megamenu JavaScript
 * Handles dropdown positioning, mobile modal functionality, sidebar, and sticky functionality
 */

document.addEventListener('DOMContentLoaded', function() {
    const menuItems = document.querySelectorAll('.tpt-megamenu__item-level--1');
    const sidebarTrigger = document.querySelector('[data-sidebar-trigger]');    
    const sidebar = document.querySelector('[data-sidebar]');
    
    const sidebarOverlay = document.querySelector('[data-sidebar-overlay]');
    const sidebarClose = document.querySelector('[data-sidebar-close]');
    const sidebarContent = document.querySelector('[data-sidebar-content]');
    const menuContainer = document.querySelector('.tpt-megamenu-container');
    const menuNavbar = document.querySelector('.tpt-megamenu-navbar');
    let sectionWrapper = null;
    let originalNavbarParent = null;
    let originalNavbarNextSibling = null;
    
    // Find the Shopify section wrapper
    if (menuContainer && menuContainer.dataset.sectionId) {
        sectionWrapper = document.getElementById(`shopify-section-${menuContainer.dataset.sectionId}`);
        // Store original navbar parent and position for potential restoration
        if (menuNavbar) {
            originalNavbarParent = menuNavbar.parentNode;
            originalNavbarNextSibling = menuNavbar.nextElementSibling;
        }
    }
    
    let resizeTimeout;
    let currentModal = null;
    let activeMenuItem = null;
    let stickyObserver = null;
    
    // Mobile detection
    function isMobile() {
        return window.innerWidth <= 768;
    }
    
    // Check if layout is horizontal - Updated to check grid elements instead of container
    function isHorizontalLayout(menuItem = null) {
        if (menuItem) {
            // Check the specific menu item's dropdown for horizontal layout
            const dropdown = menuItem.querySelector('.tpt-megamenu__dropdown');
            if (dropdown) {
                const horizontalGrid = dropdown.querySelector('.tpt-megamenu__grid.tpt-horizontal-menu');
                return !!horizontalGrid;
            }
        }
        
        // Fallback: check if any grid has horizontal layout
        const horizontalGrid = document.querySelector('.tpt-megamenu__grid.tpt-horizontal-menu');
        return !!horizontalGrid;
    }

    /**
     * Copy CSS variables from menu container to target element
     * @param {Element} targetElement - Element to copy variables to
     */
    function copyCSSVariablesToElement(targetElement) {
        if (!menuContainer || !targetElement) return;

        // Get computed styles from the menu container
        const containerStyles = window.getComputedStyle(menuContainer);
        
        // List of CSS variables that should be copied
        const variablesToCopy = [
            '--see-more-color',
            '--see-more-hover-color', 
            '--see-more-background-color',
            '--see-more-hover-background-color',
            '--see-more-padding-vertical',
            '--see-more-font-size',
            '--see-more-font-weight',
            '--sidebar-header-color',
            '--sidebar-header-background-color', 
            '--sidebar-header-padding',
            '--modal-header-background-color',
            '--modal-header-text-color',
            '--modal-header-padding',
            '--modal-header-border-color',
            '--modal-header-border-width',
            '--modal-header-title-font-size',
            '--modal-header-title-font-weight',
            '--item-text-color',
            '--item-text-hover-color',
            '--item-background-color',
            '--item-background-hover-color',
            '--item-border-radius',
            '--item-padding-vertical',
            '--item-padding-horizontal',
            '--item-font-size',
            '--item-font-family'
        ];

        // Copy each variable
        variablesToCopy.forEach(variable => {
            const value = containerStyles.getPropertyValue(variable);
            if (value) {
                targetElement.style.setProperty(variable, value);
            }
        });

        // Also copy variables from the container's style attribute directly
        if (menuContainer.hasAttribute('style')) {
            const inlineStyle = menuContainer.getAttribute('style');
            variablesToCopy.forEach(variable => {
                const regex = new RegExp(`(${variable.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*:\\s*[^;]+)`, 'i');
                const match = inlineStyle.match(regex);
                if (match) {
                    const declaration = match[1];
                    const [prop, val] = declaration.split(':').map(s => s.trim());
                    if (val) {
                        targetElement.style.setProperty(prop, val);
                    }
                }
            });
        }
    }
    
    // Initialize sticky menu functionality
    if (menuContainer && sectionWrapper && menuContainer.dataset.stickyMenu === 'true') {
        initializeStickyMenu();
    }
    
    // Initialize functionality for each menu item
    menuItems.forEach(item => {
        const dropdown = item.querySelector('.tpt-megamenu__dropdown');
        if (!dropdown) return;
        
        // Desktop hover functionality
        if (!isMobile()) {
            setupDesktopEvents(item, dropdown);
        }
        
        // Mobile click functionality
        setupMobileEvents(item, dropdown);
    });

    // Initialize sidebar functionality
    if (sidebarTrigger && sidebar) {
        initializeSidebar();
    }

    /**
     * Initialize sticky menu functionality
     */
    function initializeStickyMenu() {
        // Check if menu should be moved to header
        if (menuContainer.dataset.moveToHeader === 'true') {
            moveNavbarToHeader();
        } else {
            // Apply sticky attributes to the section wrapper
            sectionWrapper.setAttribute('data-sticky-enabled', 'true');
            
            // Copy CSS variables from menu container to section wrapper
            const stickyZIndex = menuContainer.style.getPropertyValue('--sticky-z-index') || '10';
            sectionWrapper.style.setProperty('--sticky-z-index', stickyZIndex);
        }
    }

    /**
     * Move only the megamenu navbar to header element (keep sidebar and modal in original container)
     */
    function moveNavbarToHeader() {
        if (!menuNavbar) {
            console.warn('TPT Megamenu: Navbar element not found');
            return;
        }

        let headerElement = null;
        
        // Try to find header element using custom class or default <header> tag
        if (menuContainer.dataset.headerClass) {
            headerElement = document.querySelector(`.${menuContainer.dataset.headerClass}`);
        }
        
        // Fallback to <header> tag if custom class not found or not specified
        if (!headerElement) {
            headerElement = document.querySelector('header');
        }
        
        if (headerElement) {
            // Create a wrapper for the navbar in the header to maintain styling
            const headerNavWrapper = document.createElement('div');
            headerNavWrapper.className = 'tpt-megamenu-header-wrapper';
            headerNavWrapper.style.cssText = `
                background-color: ${menuContainer.style.getPropertyValue('--container-background-color') || 'var(--container-background-color, transparent)'};
                border-width: ${menuContainer.style.getPropertyValue('--container-border-width') || 'var(--container-border-width, 0px)'};
                border-style: ${menuContainer.style.getPropertyValue('--container-border-style') || 'var(--container-border-style, solid)'};
                border-color: ${menuContainer.style.getPropertyValue('--container-border-color') || 'var(--container-border-color, #e0e0e0)'};
                border-radius: ${menuContainer.style.getPropertyValue('--container-border-radius') || 'var(--container-border-radius, 0px)'};
                padding: ${menuContainer.style.getPropertyValue('--container-padding-vertical') || 'var(--container-padding-vertical, 0px)'} ${menuContainer.style.getPropertyValue('--container-padding-horizontal') || 'var(--container-padding-horizontal, 0px)'};
                box-sizing: border-box;
            `;
            
            // Copy all CSS custom properties from the original container
            const computedStyle = window.getComputedStyle(menuContainer);
            const cssVars = Array.from(document.documentElement.style).filter(prop => prop.startsWith('--'));
            cssVars.forEach(prop => {
                const value = menuContainer.style.getPropertyValue(prop);
                if (value) {
                    headerNavWrapper.style.setProperty(prop, value);
                }
            });
            
            // Also copy inline CSS variables
            if (menuContainer.getAttribute('style')) {
                const inlineStyles = menuContainer.getAttribute('style');
                const vars = inlineStyles.match(/--[^;]+;?/g) || [];
                vars.forEach(varDecl => {
                    headerNavWrapper.style.cssText += varDecl;
                });
            }
            
            // Move only the navbar to the header wrapper
            headerNavWrapper.appendChild(menuNavbar);
            headerElement.appendChild(headerNavWrapper);
            
            // Apply sticky properties to the header
            headerElement.setAttribute('data-tpt-megamenu-moved', 'true');
            
            // Copy CSS variables for z-index
            const stickyZIndex = menuContainer.style.getPropertyValue('--sticky-z-index') || '10';
            headerElement.style.setProperty('--tpt-megamenu-z-index', stickyZIndex);
            headerNavWrapper.style.setProperty('--sticky-z-index', stickyZIndex);
            
            // Ensure dropdowns have proper z-index when in header
            headerNavWrapper.style.setProperty('--dropdown-z-index', `calc(${stickyZIndex} + 1)`);
            
            console.log('TPT Megamenu: Successfully moved navbar to header element');
            
            // Hide the original section wrapper since navbar is now moved
            
            // Keep sidebar and modal in the original container in body
            // They will remain with proper z-index stacking
            
        } else {
            // Header not found, log warning and fallback to original behavior
            console.warn('TPT Megamenu: Header element not found. Menu will remain in original position.');
            
            // Fallback to original sticky behavior
            sectionWrapper.setAttribute('data-sticky-enabled', 'true');
            const stickyZIndex = menuContainer.style.getPropertyValue('--sticky-z-index') || '10';
            sectionWrapper.style.setProperty('--sticky-z-index', stickyZIndex);
        }
    }

    /**
     * Restore navbar to original position (if needed)
     */
    function restoreNavbarToOriginalPosition() {
        if (!originalNavbarParent || !menuNavbar) return;
        
        const currentHeaderWrapper = menuNavbar.closest('.tpt-megamenu-header-wrapper');
        const currentHeader = menuNavbar.closest('header, [data-tpt-megamenu-moved]');
        
        if (currentHeaderWrapper && currentHeader && menuNavbar.parentNode !== originalNavbarParent) {
            // Remove attributes from header
            currentHeader.removeAttribute('data-tpt-megamenu-moved');
            currentHeader.style.removeProperty('--tpt-megamenu-z-index');
            
            // Move navbar back to original position
            if (originalNavbarNextSibling) {
                originalNavbarParent.insertBefore(menuNavbar, originalNavbarNextSibling);
            } else {
                originalNavbarParent.appendChild(menuNavbar);
            }
            
            // Remove the header wrapper
            if (currentHeaderWrapper.parentNode) {
                currentHeaderWrapper.parentNode.removeChild(currentHeaderWrapper);
            }
            
            // Show the section wrapper again
            if (sectionWrapper) {
                sectionWrapper.style.display = '';
            }
            
            console.log('TPT Megamenu: Restored navbar to original position');
        }
    }

    /**
     * Debounce function to limit function calls
     */
    function debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }
    
    /**
     * Setup desktop hover events
     */
    function setupDesktopEvents(item, dropdown) {
        // Position dropdown on hover
        item.addEventListener('mouseenter', function() {
            if (isMobile()) return;
            
            // Small delay to ensure dropdown is visible for measurement
            setTimeout(() => {
                positionDropdown(item, dropdown);
            }, 10);
        });
        
        // Reset positioning when hover ends - Skip for horizontal layout
        item.addEventListener('mouseleave', function() {
            if (isMobile()) return;
            
            // Skip reset for horizontal layout - now checking specific menu item
            if (isHorizontalLayout(item)) return;
            
            resetDropdownPosition(dropdown);
        });
    }
    
    /**
     * Setup mobile click events
     */
    function setupMobileEvents(item, dropdown) {
        const itemLabel = item.querySelector('.tpt-megamenu__item-label');
        
        if (itemLabel) {
            itemLabel.addEventListener('click', function(e) {
                if (!isMobile()) return;
                
                e.preventDefault();
                e.stopPropagation();
                
                // Close any existing modal first
                if (currentModal) {
                    closeModal();
                }
                
                // Set active menu item for arrow state
                activeMenuItem = item;
                item.classList.add('active');
                
                // Create and open new modal
                openModal(item, dropdown);
            });
        }
    }

    /**
     * Initialize sidebar functionality
     */
    function initializeSidebar() {
        // Copy CSS variables to sidebar when initializing
        copyCSSVariablesToElement(sidebar);
        
        // Set sidebar position based on data attribute
        const sidebarPosition = sidebar.dataset.position || 'right';
        
        // Sidebar trigger click - Use both click and touchstart for mobile
        if (sidebarTrigger) {
            const handleSidebarTrigger = function(e) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                
                // Additional check to prevent any default behavior
                if (e.type === 'click' && e.target.tagName === 'BUTTON') {
                    e.target.blur(); // Remove focus to prevent space/enter key issues
                }
                
                openSidebar();
                return false;
            };
            
            sidebarTrigger.addEventListener('click', handleSidebarTrigger);
            sidebarTrigger.addEventListener('touchstart', handleSidebarTrigger, { passive: false });
        }

        // Sidebar overlay click
        if (sidebarOverlay) {
            sidebarOverlay.addEventListener('click', function(e) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                closeSidebar();
                return false;
            });
        }

        // Sidebar close button click
        if (sidebarClose) {
            sidebarClose.addEventListener('click', function(e) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                closeSidebar();
                return false;
            });
        }

        // Prevent sidebar panel clicks from closing the sidebar
        const sidebarPanel = sidebar.querySelector('.tpt-megamenu__sidebar-panel');
        if (sidebarPanel) {
            sidebarPanel.addEventListener('click', function(e) {
                e.stopPropagation();
            });
        }

        // Close sidebar on escape key
        document.addEventListener('keydown', function(e) {
            if (e.key === 'Escape' && sidebar.classList.contains('active')) {
                closeSidebar();
            }
        });

        // Populate sidebar content initially
        populateSidebarContent();
    }

    /**
     * Open sidebar
     */
    function openSidebar() {
        if (!sidebar) return;
        
        // Copy CSS variables each time sidebar opens to ensure latest values
        copyCSSVariablesToElement(sidebar);
        
        sidebar.classList.add('active');
        document.body.classList.add('tpt-sidebar-open');
        document.body.style.overflow = 'hidden';
        
        // Populate content when opening
        populateSidebarContent();
        
        // Focus management for accessibility
        setTimeout(() => {
            if (sidebarClose) {
                sidebarClose.focus();
            }
        }, 300);
    }

    /**
     * Close sidebar
     */
    function closeSidebar() {
        if (!sidebar) return;
        
        sidebar.classList.remove('active');
        document.body.classList.remove('tpt-sidebar-open');
        document.body.style.overflow = '';
        
        // Return focus to trigger button
        if (sidebarTrigger) {
            sidebarTrigger.focus();
        }
    }

    /**
     * Filter blocks that should show in sidebar
     */
    function filterSidebarBlocks(dropdown) {
        const dropdownClone = dropdown.cloneNode(true);
        
        // Find all grid blocks within the cloned dropdown
        const gridBlocks = dropdownClone.querySelectorAll('.tpt-megamenu__grid[data-show-in-sidebar]');
        
        // Remove blocks that shouldn't show in sidebar
        gridBlocks.forEach(block => {
            const showInSidebar = block.getAttribute('data-show-in-sidebar');
            if (showInSidebar === 'false') {
                block.remove();
            }
        });
        
        // Check if there are any remaining blocks
        const remainingBlocks = dropdownClone.querySelectorAll('.tpt-megamenu__grid');
        
        // If no blocks remain, return null to indicate this menu item shouldn't show in sidebar
        if (remainingBlocks.length === 0) {
            return null;
        }
        
        return dropdownClone;
    }

    /**
     * Populate sidebar with menu content
     */
    function populateSidebarContent() {
        if (!sidebarContent) return;

        // Clear existing content
        sidebarContent.innerHTML = '';

        // Get all menu items that have dropdowns - search in both original container and moved navbar
        let currentMenuItems = Array.from(document.querySelectorAll('.tpt-megamenu__item-level--1')).filter(item => {
            return item.querySelector('.tpt-megamenu__dropdown');
        });

        // Get extra blocks that don't have corresponding navigation links
        const extraBlocksContainer = document.querySelector('[data-extra-blocks]');
        let extraBlocks = [];
        if (extraBlocksContainer) {
            extraBlocks = Array.from(extraBlocksContainer.querySelectorAll('.tpt-megamenu__extra-block'));
        }

        // Track if any menu items were added to sidebar
        let hasMenuItems = false;

        // Create sidebar menu structure for regular menu items
        currentMenuItems.forEach((item, index) => {
            const menuTitle = item.dataset.menuTitle || item.querySelector('.tpt-megamenu__item-level--1__text')?.textContent?.trim() || 'Menu Item';
            const dropdown = item.querySelector('.tpt-megamenu__dropdown');
            
            if (!dropdown) return;

            // Filter blocks based on show_in_sidebar setting
            const filteredDropdown = filterSidebarBlocks(dropdown);
            
            // Skip this menu item if no blocks should show in sidebar
            if (!filteredDropdown) return;

            // Create menu item container
            const sidebarMenuItem = document.createElement('div');
            sidebarMenuItem.className = 'tpt-megamenu__sidebar-menu-item';
            sidebarMenuItem.dataset.menuIndex = index;

            // Create menu header (clickable title)
            const menuHeader = document.createElement('div');
            menuHeader.className = 'tpt-megamenu__sidebar-menu-header';
            menuHeader.innerHTML = `
                <h3 class="tpt-megamenu__sidebar-menu-title">${menuTitle}</h3>
                <svg class="tpt-megamenu__sidebar-menu-arrow" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            `;

            // Create menu content container
            const menuContent = document.createElement('div');
            menuContent.className = 'tpt-megamenu__sidebar-menu-content';

            // Use filtered dropdown content
            filteredDropdown.style.cssText = '';
            filteredDropdown.classList.add('tpt-megamenu__sidebar-dropdown');
            
            // Remove dropdown-specific positioning classes
            filteredDropdown.classList.remove(
                'tpt-megamenu__dropdown--shifted',
                'tpt-megamenu__dropdown--right-aligned',
                'tpt-megamenu__dropdown--centered',
                'tpt-megamenu__dropdown--constrained'
            );

            menuContent.appendChild(filteredDropdown);

            // Add click event to toggle menu item
            menuHeader.addEventListener('click', function() {
                // Simply toggle current item without closing others
                sidebarMenuItem.classList.toggle('expanded');
                
                // Update aria attributes for accessibility
                menuHeader.setAttribute('aria-expanded', sidebarMenuItem.classList.contains('expanded'));
            });

            // Set initial aria attributes
            menuHeader.setAttribute('role', 'button');
            menuHeader.setAttribute('aria-expanded', 'false');
            menuHeader.setAttribute('tabindex', '0');

            // Keyboard support for menu header
            menuHeader.addEventListener('keydown', function(e) {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    menuHeader.click();
                }
            });

            // Assemble menu item
            sidebarMenuItem.appendChild(menuHeader);
            sidebarMenuItem.appendChild(menuContent);
            sidebarContent.appendChild(sidebarMenuItem);

            // Expand ALL items by default
            setTimeout(() => {
                sidebarMenuItem.classList.add('expanded');
                menuHeader.setAttribute('aria-expanded', 'true');
            }, 100);
            
            hasMenuItems = true;
        });

        // Process extra blocks that don't have corresponding navigation links
        extraBlocks.forEach((extraBlock, index) => {
            const dropdown = extraBlock.querySelector('.tpt-megamenu__dropdown');
            if (!dropdown) return;

            // Get block title from the grid data attribute or fallback to default
            const gridElement = dropdown.querySelector('.tpt-megamenu__grid[data-block-title]');
            const blockTitle = gridElement?.getAttribute('data-block-title') || extraBlock.dataset.blockTitle || `Extra Block ${index + 1}`;
            
            // Filter blocks based on show_in_sidebar setting
            const filteredDropdown = filterSidebarBlocks(dropdown);
            
            // Skip this extra block if no content should show in sidebar
            if (!filteredDropdown) return;

            // Create menu item container for extra block (remove the extra class)
            const sidebarMenuItem = document.createElement('div');
            sidebarMenuItem.className = 'tpt-megamenu__sidebar-menu-item';
            sidebarMenuItem.dataset.extraBlockIndex = index;

            // Create menu header (clickable title)
            const menuHeader = document.createElement('div');
            menuHeader.className = 'tpt-megamenu__sidebar-menu-header';
            menuHeader.innerHTML = `
                <h3 class="tpt-megamenu__sidebar-menu-title">${blockTitle}</h3>
                <svg class="tpt-megamenu__sidebar-menu-arrow" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            `;

            // Create menu content container
            const menuContent = document.createElement('div');
            menuContent.className = 'tpt-megamenu__sidebar-menu-content';

            // Use filtered dropdown content
            filteredDropdown.style.cssText = '';
            filteredDropdown.classList.add('tpt-megamenu__sidebar-dropdown');
            
            // Remove dropdown-specific positioning classes
            filteredDropdown.classList.remove(
                'tpt-megamenu__dropdown--shifted',
                'tpt-megamenu__dropdown--right-aligned',
                'tpt-megamenu__dropdown--centered',
                'tpt-megamenu__dropdown--constrained'
            );

            menuContent.appendChild(filteredDropdown);

            // Add click event to toggle menu item
            menuHeader.addEventListener('click', function() {
                // Simply toggle current item without closing others
                sidebarMenuItem.classList.toggle('expanded');
                
                // Update aria attributes for accessibility
                menuHeader.setAttribute('aria-expanded', sidebarMenuItem.classList.contains('expanded'));
            });

            // Set initial aria attributes
            menuHeader.setAttribute('role', 'button');
            menuHeader.setAttribute('aria-expanded', 'false');
            menuHeader.setAttribute('tabindex', '0');

            // Keyboard support for menu header
            menuHeader.addEventListener('keydown', function(e) {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    menuHeader.click();
                }
            });

            // Assemble menu item
            sidebarMenuItem.appendChild(menuHeader);
            sidebarMenuItem.appendChild(menuContent);
            sidebarContent.appendChild(sidebarMenuItem);

            // Expand ALL extra blocks by default
            setTimeout(() => {
                sidebarMenuItem.classList.add('expanded');
                menuHeader.setAttribute('aria-expanded', 'true');
            }, 100);
            
            hasMenuItems = true;
        });

        // Show message if no menu items were added
        if (!hasMenuItems) {
            sidebarContent.innerHTML = '<div style="padding: 20px; text-align: center; color: #666;">No sidebar content available</div>';
        }
    }
    
    /**
     * Open mobile modal
     */
    function openModal(menuItem, dropdown) {
        const menuTitle = menuItem.querySelector('.tpt-megamenu__item-level--1__text').textContent.trim();
        
        // Create modal overlay
        const overlay = document.createElement('div');
        overlay.className = 'tpt-megamenu__modal-overlay';
        
        // Create modal
        const modal = document.createElement('div');
        modal.className = 'tpt-megamenu__modal tpt-megamenu';
        
        // Copy CSS variables to modal
        copyCSSVariablesToElement(modal);
        
        // Create modal header
        const header = document.createElement('div');
        header.className = 'tpt-megamenu__modal-header';
        
        const title = document.createElement('h3');
        title.className = 'tpt-megamenu__modal-title';
        title.textContent = menuTitle;
        
        const closeBtn = document.createElement('button');
        closeBtn.className = 'tpt-megamenu__modal-close';
        closeBtn.innerHTML = '×';
        closeBtn.setAttribute('aria-label', 'Close menu');
        
        header.appendChild(title);
        header.appendChild(closeBtn);
        
        // Create modal content
        const content = document.createElement('div');
        content.className = 'tpt-megamenu__modal-content';
        
        // Clone dropdown content
        const dropdownClone = dropdown.cloneNode(true);
        dropdownClone.style.visibility = 'visible';
        dropdownClone.style.opacity = '1';
        dropdownClone.style.position = 'relative';
        dropdownClone.style.top = 'auto';
        dropdownClone.style.left = 'auto';
        dropdownClone.style.width = 'auto';
        dropdownClone.style.minWidth = 'auto';
        dropdownClone.style.boxShadow = 'none';
        
        content.appendChild(dropdownClone);
        
        // Assemble modal
        modal.appendChild(header);
        modal.appendChild(content);
        
        // Add to DOM
        document.body.appendChild(overlay);
        document.body.appendChild(modal);
        
        // Store current modal reference
        currentModal = { overlay, modal };
        
        // Prevent body scroll
        document.body.style.overflow = 'hidden';
        
        // Animate in
        requestAnimationFrame(() => {
            overlay.classList.add('active');
            modal.classList.add('active');
        });
        
        // Setup close events
        closeBtn.addEventListener('click', closeModal);
        overlay.addEventListener('click', closeModal);
        
        // Close on escape key
        document.addEventListener('keydown', handleEscapeKey);
    }
    
    /**
     * Close mobile modal
     */
    function closeModal() {
        if (!currentModal) return;
        
        const { overlay, modal } = currentModal;
        
        // Remove active state from menu item and reset arrow
        if (activeMenuItem) {
            activeMenuItem.classList.remove('active');
            activeMenuItem = null;
        }
        
        // Animate out
        overlay.classList.remove('active');
        modal.classList.remove('active');
        
        // Remove from DOM after animation
        setTimeout(() => {
            if (overlay.parentNode) {
                overlay.parentNode.removeChild(overlay);
            }
            if (modal.parentNode) {
                modal.parentNode.removeChild(modal);
            }
            
            // Restore body scroll
            document.body.style.overflow = '';
            
            // Clear current modal reference
            currentModal = null;
            
            // Remove escape key listener
            document.removeEventListener('keydown', handleEscapeKey);
        }, 300);
    }
    
    /**
     * Handle escape key press
     */
    function handleEscapeKey(e) {
        if (e.key === 'Escape' || e.keyCode === 27) {
            closeModal();
        }
    }
    
    /**
     * Position dropdown to prevent overflow (Desktop only)
     * @param {Element} menuItem - The menu item element
     * @param {Element} dropdown - The dropdown element
     */
    function positionDropdown(menuItem, dropdown) {
        if (isMobile()) return;
        
        // Skip positioning for horizontal layout - now checking specific menu item
        if (isHorizontalLayout(menuItem)) return;
        
        // Reset any previous positioning
        resetDropdownPosition(dropdown);
        
        // Get viewport dimensions
        const viewportWidth = window.innerWidth;
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        const effectiveViewportWidth = viewportWidth - scrollbarWidth;
        
        // Get positions and dimensions
        const menuItemRect = menuItem.getBoundingClientRect();
        const dropdownRect = dropdown.getBoundingClientRect();
        const dropdownWidth = dropdownRect.width;
        
        // Calculate potential overflow
        const rightEdge = menuItemRect.left + dropdownWidth;
        const leftEdge = menuItemRect.left;
        
        // Add safety margins
        const rightMargin = 20;
        const leftMargin = 20;
        
        // Check for right edge overflow
        if (rightEdge > (effectiveViewportWidth - rightMargin)) {
            // Calculate how much we need to shift left
            const overflow = rightEdge - (effectiveViewportWidth - rightMargin);
            const shiftLeft = Math.min(overflow, menuItemRect.right - dropdownWidth - leftMargin);
            
            if (shiftLeft > 0) {
                dropdown.style.left = `${-shiftLeft}px`;
                dropdown.classList.add('tpt-megamenu__dropdown--shifted');
            } else {
                // If we can't shift enough, align to right edge of menu item
                dropdown.style.left = 'auto';
                dropdown.style.right = '0';
                dropdown.classList.add('tpt-megamenu__dropdown--right-aligned');
            }
        }
        
        // Check for left edge overflow after right alignment
        const currentDropdownRect = dropdown.getBoundingClientRect();
        if (currentDropdownRect.left < leftMargin) {
            // Reset and try centering approach
            resetDropdownPosition(dropdown);
            
            const availableWidth = effectiveViewportWidth - leftMargin - rightMargin;
            const centerPosition = (effectiveViewportWidth / 2) - (dropdownWidth / 2) - menuItemRect.left;
            
            // Ensure dropdown doesn't exceed available space
            if (dropdownWidth <= availableWidth) {
                dropdown.style.left = `${Math.max(-menuItemRect.left + leftMargin, centerPosition)}px`;
                dropdown.classList.add('tpt-megamenu__dropdown--centered');
            } else {
                // As last resort, constrain width and center
                dropdown.style.left = `${-menuItemRect.left + leftMargin}px`;
                dropdown.style.maxWidth = `${availableWidth}px`;
                dropdown.classList.add('tpt-megamenu__dropdown--constrained');
            }
        }
    }
    
    /**
     * Reset dropdown to default position
     * @param {Element} dropdown - The dropdown element
     */
    function resetDropdownPosition(dropdown) {
        // Skip reset for horizontal layout - need to check parent menu item
        const menuItem = dropdown.closest('.tpt-megamenu__item-level--1');
        if (isHorizontalLayout(menuItem)) return;
        
        dropdown.style.left = '';
        dropdown.style.right = '';
        dropdown.style.maxWidth = '';
        dropdown.classList.remove(
            'tpt-megamenu__dropdown--shifted',
            'tpt-megamenu__dropdown--right-aligned',
            'tpt-megamenu__dropdown--centered',
            'tpt-megamenu__dropdown--constrained'
        );
    }
    
    /**
     * Recalculate positions on window resize
     */
    function handleResize() {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
            // Close mobile modal on resize to desktop
            if (!isMobile() && currentModal) {
                closeModal();
            }

            // Close sidebar on significant resize changes
            if (sidebar && sidebar.classList.contains('active')) {
                closeSidebar();
            }
            
            // Find currently visible dropdowns and reposition them (desktop only)
            if (!isMobile()) {
                const visibleDropdowns = document.querySelectorAll('.tpt-megamenu__item-level--1:hover .tpt-megamenu__dropdown');
                visibleDropdowns.forEach(dropdown => {
                    const menuItem = dropdown.closest('.tpt-megamenu__item-level--1');
                    if (menuItem && !isHorizontalLayout(menuItem)) {
                        positionDropdown(menuItem, dropdown);
                    }
                });
            }
            
            // Re-setup events based on new screen size - get current menu items from wherever they are
            const currentMenuItems = document.querySelectorAll('.tpt-megamenu__item-level--1');
            currentMenuItems.forEach(item => {
                const dropdown = item.querySelector('.tpt-megamenu__dropdown');
                if (!dropdown) return;
                
                // Remove all existing event listeners by cloning and replacing
                const newItem = item.cloneNode(true);
                item.parentNode.replaceChild(newItem, item);
                
                // Re-setup events
                if (!isMobile()) {
                    setupDesktopEvents(newItem, newItem.querySelector('.tpt-megamenu__dropdown'));
                }
                setupMobileEvents(newItem, newItem.querySelector('.tpt-megamenu__dropdown'));
            });

            // Repopulate sidebar content after resize
            if (sidebar && sidebar.classList.contains('active')) {
                populateSidebarContent();
            }

            // Handle header movement on resize if needed
            if (menuContainer && menuContainer.dataset.moveToHeader === 'true') {
                // Check if we need to move or restore based on current state
                const isInHeader = !!menuNavbar && !!menuNavbar.closest('header, [data-tpt-megamenu-moved]');
                const shouldBeInHeader = true; // Based on settings, always true if moveToHeader is set
                
                if (shouldBeInHeader && !isInHeader) {
                    moveNavbarToHeader();
                } else if (!shouldBeInHeader && isInHeader) {
                    restoreNavbarToOriginalPosition();
                }
            }
        }, 100);
    }
    
    window.addEventListener('resize', handleResize);
    
    window.addEventListener('orientationchange', () => {
        setTimeout(handleResize, 500);
    });
    
    // Close modal when clicking outside menu area
    document.addEventListener('click', function(e) {
        if (isMobile() && currentModal && !e.target.closest('.tpt-megamenu')) {
            // Don't close if clicking on modal content
            if (!e.target.closest('.tpt-megamenu__modal')) {
                closeModal();
            }
        }

        // Close sidebar when clicking outside sidebar area (but not on trigger)
        if (sidebar && sidebar.classList.contains('active') && 
            !e.target.closest('.tpt-megamenu__sidebar-panel') && 
            !e.target.closest('[data-sidebar-trigger]')) {
            // Only close if clicking on the overlay specifically
            if (e.target.matches('[data-sidebar-overlay]') || e.target.closest('[data-sidebar-overlay]')) {
                closeSidebar();
            }
        }
    });
    
    // Handle keyboard navigation for accessibility - using current menu items
    function updateMenuItemsAccessibility() {
        const currentMenuItems = document.querySelectorAll('.tpt-megamenu__item-level--1');
        currentMenuItems.forEach(item => {
            const itemLabel = item.querySelector('.tpt-megamenu__item-label');
            if (itemLabel && !itemLabel.hasAttribute('data-accessibility-setup')) {
                itemLabel.addEventListener('keydown', function(e) {
                    // Enter or Space key to open dropdown
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        if (isMobile()) {
                            // Trigger click event for mobile
                            itemLabel.click();
                        }
                    }
                });
                
                // Make it focusable
                itemLabel.setAttribute('tabindex', '0');
                itemLabel.setAttribute('role', 'button');
                itemLabel.setAttribute('aria-haspopup', 'true');
                itemLabel.setAttribute('aria-expanded', 'false');
                itemLabel.setAttribute('data-accessibility-setup', 'true');
            }
        });
    }

    // Initialize accessibility
    updateMenuItemsAccessibility();

    // Sidebar keyboard navigation
    if (sidebarTrigger) {
        sidebarTrigger.addEventListener('keydown', function(e) {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                sidebarTrigger.click();
            }
        });
    }

    // Clean up observers on page unload
    window.addEventListener('beforeunload', function() {
        if (stickyObserver) {
            stickyObserver.disconnect();
        }
    });
});

/**
 * Utility function to check if element is in viewport
 * @param {Element} element - Element to check
 * @returns {boolean} - Whether element is fully in viewport
 */
function isInViewport(element) {
    const rect = element.getBoundingClientRect();
    return (
        rect.left >= 0 &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth) &&
        rect.top >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientWidth)
    );
}